import { useState, useEffect, useCallback, useRef } from 'react';
import auditService, { 
  ReceiptAudit, 
  ReceiptAuditsResponse, 
  ReceiptAuditsParams,
  UploadReceiptResponse
} from '../services/auditService';

interface UseAuditReturn {
  audits: ReceiptAudit[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  size: number;
  refreshAudits: () => Promise<void>;
  uploadReceipt: (file: File, storeId: string) => Promise<UploadReceiptResponse>;
  setFilters: (filters: ReceiptAuditsParams) => void;
  setError: (error: string | null) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
}

export const useAudit = (
  initialParams: ReceiptAuditsParams = {},
  options: { initialPage?: number; initialSize?: number } = {},
): UseAuditReturn => {
  const [audits, setAudits] = useState<ReceiptAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(options.initialPage ?? 1);
  const [size, setSize] = useState(options.initialSize ?? 25);
  const [filters, setFilters] = useState<ReceiptAuditsParams>(initialParams);
  const latestRequestIdRef = useRef(0);

  const fetchAudits = useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        size,
        ...filters,
      };
      
      const response: ReceiptAuditsResponse = await auditService.getReceiptAudits(params);

      if (latestRequestIdRef.current !== requestId) {
        return;
      }

      setAudits(response.items);
      setTotal(response.total);
    } catch (err: any) {
      if (latestRequestIdRef.current !== requestId) {
        return;
      }

      setError(err?.message || 'Failed to fetch audits');
      console.error('Error fetching audits:', err);
    } finally {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [page, size, filters]);

  const refreshAudits = useCallback(async () => {
    await fetchAudits();
  }, [fetchAudits]);

  const uploadReceipt = useCallback(async (file: File, storeId: string): Promise<UploadReceiptResponse> => {
    try {
      const response = await auditService.uploadReceipt(file, storeId);
      await refreshAudits();
      return response;
    } catch (err: any) {
      throw new Error(err?.message || 'Failed to upload receipt');
    }
  }, [refreshAudits]);

  const updateFilters = useCallback((newFilters: ReceiptAuditsParams) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const setPageHandler = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const setSizeHandler = useCallback((newSize: number) => {
    setSize(newSize);
    setPage(1);
  }, []);

  const setErrorHandler = useCallback((newError: string | null) => {
    setError(newError);
  }, []);

  // Fetch audits when page, size, or filters change
  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  return {
    audits,
    loading,
    error,
    total,
    page,
    size,
    refreshAudits,
    uploadReceipt,
    setFilters: updateFilters,
    setError: setErrorHandler,
    setPage: setPageHandler,
    setSize: setSizeHandler,
  };
}; 
